import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccessCheck,
  AccessConfig,
  AccessReportRow,
  AccessRole,
  ActivityPage,
  ActivityQuery,
  UsageStats,
  TreasuryReport,
  CreateManualCaseInput,
  OfficeCommunity,
  UpdateManualCaseInput,
  DisplayConfig,
  CacheStats,
  ScoringConfig,
  ScoringVersion,
  SnapshotRunInfo,
  Agreement,
  CreateAgreementInput,
  Me,
  Office,
  OfficeBoard,
  SnapshotInfo,
  TandemSheet,
  TandemSheetInput,
  TimelineEntry,
  UpdateAgreementInput,
  ObjectType,
} from '@odd/domain';
import { api, apiRaw, send } from './api';

export interface PublicConfig {
  elevauthUrl: string;
  domain: string;
  /** Web del ERP para los enlaces (comunidades, juntas, tickets) */
  erpUrl?: string;
}

export const qk = {
  config: ['config'] as const,
  me: ['me'] as const,
  offices: ['offices'] as const,
  board: (id: string, run?: string | null) =>
    run ? (['board', id, run] as const) : (['board', id] as const),
  history: (id: string) => ['history', id] as const,
  timeline: (t: string, id: string) => ['timeline', t, id] as const,
};

export const useConfig = () =>
  useQuery({ queryKey: qk.config, queryFn: () => api<PublicConfig>('/api/config'), staleTime: Infinity });

export const useMe = (enabled: boolean) =>
  useQuery({ queryKey: qk.me, queryFn: () => api<Me>('/api/me'), enabled, staleTime: 5 * 60_000 });

export const useOffices = (enabled: boolean) =>
  useQuery({
    queryKey: qk.offices,
    queryFn: () => api<Office[]>('/api/offices'),
    enabled,
    staleTime: 10 * 60_000,
  });

export const useBoard = (officeId: string | null, runId: string | null = null) =>
  useQuery({
    queryKey: qk.board(officeId ?? '', runId),
    queryFn: () => api<OfficeBoard>(`/api/offices/${officeId}/board${runId ? `?run=${runId}` : ''}`),
    enabled: !!officeId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

export const useHistory = (officeId: string | null) =>
  useQuery({
    queryKey: qk.history(officeId ?? ''),
    queryFn: () => api<SnapshotInfo[]>(`/api/offices/${officeId}/snapshots`),
    enabled: !!officeId,
    staleTime: 60_000,
  });

export const useTimeline = (objectType: ObjectType, objectId: string) =>
  useQuery({
    queryKey: qk.timeline(objectType, objectId),
    queryFn: () => api<TimelineEntry[]>(`/api/cases/${objectType}/${objectId}/timeline`),
    staleTime: 5 * 60_000,
  });

function useBoardMutation<V, R>(officeId: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.board(officeId) }),
  });
}

export const useCreateAgreement = (officeId: string) =>
  useBoardMutation(officeId, (v: CreateAgreementInput) =>
    send<Agreement>('POST', `/api/offices/${officeId}/agreements`, v),
  );

export const useUpdateAgreement = (officeId: string) =>
  useBoardMutation(officeId, ({ id, ...v }: UpdateAgreementInput & { id: string }) =>
    send<Agreement>('PATCH', `/api/agreements/${id}`, v),
  );

export const useSaveTandemSheet = (officeId: string) =>
  useBoardMutation(officeId, (v: TandemSheetInput) =>
    send<TandemSheet>('PUT', `/api/offices/${officeId}/tandem-sheets`, v),
  );

export const useDeleteTandemSheet = (officeId: string) =>
  useBoardMutation(officeId, (id: string) => send<void>('DELETE', `/api/tandem-sheets/${id}`));

export const useSetSensitive = (officeId: string) =>
  useBoardMutation(officeId, (v: { meetingId: string; sensitive: boolean }) =>
    send('PUT', `/api/meetings/${v.meetingId}/sensitive`, { sensitive: v.sensitive }),
  );

export const useSaveSettings = (officeId: string) =>
  useBoardMutation(officeId, (v: { ideaFuerza: string | null }) =>
    send('PUT', `/api/offices/${officeId}/settings`, v),
  );

export const useSaveRuleParams = (officeId: string) =>
  useBoardMutation(
    officeId,
    (
      v: {
        ruleId: string;
        amber: number;
        red: number;
        enabled: boolean;
        fallback?: { amber: number; red: number };
      }[],
    ) => send('PUT', '/api/rule-params', v),
  );

export const useTakeSnapshot = (officeId: string) =>
  useBoardMutation(officeId, () => send('POST', '/api/snapshots', { officeId }));

/* ---------------- admin ---------------- */

export const useAdminScoring = (enabled: boolean) =>
  useQuery({
    queryKey: ['admin', 'scoring'],
    queryFn: () => api<{ current: ScoringVersion | null; versions: ScoringVersion[] }>('/api/admin/scoring'),
    enabled,
  });

export const useSaveScoring = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { config: ScoringConfig; note?: string }) =>
      send<ScoringVersion>('PUT', '/api/admin/scoring', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'scoring'] });
      qc.invalidateQueries({ queryKey: ['board'] });
    },
  });
};

export const useAdminRuns = (enabled: boolean) =>
  useQuery({
    queryKey: ['admin', 'runs'],
    queryFn: () => api<SnapshotRunInfo[]>('/api/admin/runs'),
    enabled,
  });

export const useCacheStats = (enabled: boolean) =>
  useQuery({ queryKey: ['admin', 'cache'], queryFn: () => api<CacheStats>('/api/admin/cache'), enabled });

/** Tras vaciar la caché o regenerar la foto, se refresca todo lo que muestra la web */
const useAdminAction = <R>(fn: () => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries() });
};
export const useClearCache = () =>
  useAdminAction(() => send<{ cleared: number }>('POST', '/api/admin/cache/clear'));
export const useRegenerate = () => useAdminAction(() => send<SnapshotRunInfo>('POST', '/api/snapshots', {}));

export const useAdminDisplay = (enabled: boolean) =>
  useQuery({
    queryKey: ['admin', 'display'],
    queryFn: () =>
      api<{ config: DisplayConfig; updatedBy: string | null; updatedAt: string | null }>(
        '/api/admin/display',
      ),
    enabled,
  });

export const useSaveDisplay = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: DisplayConfig) => send('PUT', '/api/admin/display', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'display'] });
      qc.invalidateQueries({ queryKey: ['board'] });
    },
  });
};

/* ---------------- accesos (quién entra y qué ve) ---------------- */

export const useAccessRoles = () =>
  useQuery({
    queryKey: ['admin', 'access'],
    queryFn: () => api<{ roles: AccessRole[]; config: AccessConfig }>('/api/admin/access'),
  });

export const useSaveAccess = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: AccessConfig) =>
      send<{ roles: AccessRole[]; config: AccessConfig }>('PUT', '/api/admin/access', c),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'access'] }),
  });
};

export const useAccessCheck = (email: string | null) =>
  useQuery({
    queryKey: ['admin', 'access', 'check', email],
    queryFn: () => api<AccessCheck>(`/api/admin/access/check?email=${encodeURIComponent(email!)}`),
    enabled: !!email,
  });

export const useAccessReport = (enabled: boolean) =>
  useQuery({
    queryKey: ['admin', 'access', 'report'],
    queryFn: () => api<AccessReportRow[]>('/api/admin/access/report'),
    enabled,
    staleTime: 5 * 60_000,
  });

/* ---------------- actividad (auditoría y uso) ---------------- */

/** Query string sin los filtros vacíos */
export const activityParams = (q: Partial<ActivityQuery>) =>
  new URLSearchParams(
    Object.entries(q)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => [k, String(v)]),
  ).toString();

export const useActivity = (q: Partial<ActivityQuery>) =>
  useQuery({
    queryKey: ['admin', 'activity', q],
    queryFn: () => api<ActivityPage>(`/api/admin/activity?${activityParams(q)}`),
    placeholderData: (prev) => prev,
  });

export const useUsage = (days: number) =>
  useQuery({
    queryKey: ['admin', 'usage', days],
    queryFn: () => api<UsageStats>(`/api/admin/usage?days=${days}`),
    placeholderData: (prev) => prev,
  });

/** Descarga el CSV del registro con los filtros actuales */
export async function downloadActivityCsv(q: Partial<ActivityQuery>) {
  const { limit: _l, offset: _o, ...filters } = q;
  const res = await apiRaw(`/api/admin/activity/export?${activityParams(filters)}`);
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), {
    href: url,
    download: `actividad-${new Date().toISOString().slice(0, 10)}.csv`,
  });
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------------- casos manuales ---------------- */

export const useOfficeCommunities = (officeId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['communities', officeId],
    queryFn: () => api<OfficeCommunity[]>(`/api/offices/${officeId}/communities`),
    enabled,
    staleTime: 30 * 60_000,
  });

export const useCreateManualCase = (officeId: string) =>
  useBoardMutation(officeId, (v: CreateManualCaseInput) =>
    send<{ case: { id: string }; sheetError: string | null }>(
      'POST',
      `/api/offices/${officeId}/manual-cases`,
      v,
    ),
  );

export const useUpdateManualCase = (officeId: string) =>
  useBoardMutation(officeId, ({ id, ...v }: UpdateManualCaseInput & { id: string }) =>
    send('PATCH', `/api/manual-cases/${id}`, v),
  );

export const useDeleteAgreement = (officeId: string) =>
  useBoardMutation(officeId, (id: string) => send<void>('DELETE', `/api/agreements/${id}`));

export const useTreasury = (officeId: string, runId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['treasury', officeId, runId],
    queryFn: () => api<TreasuryReport>(`/api/offices/${officeId}/treasury${runId ? `?run=${runId}` : ''}`),
    enabled,
    staleTime: 5 * 60_000,
  });
